import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import Loader from '../../Loader';
import useCommentBank from '../../../hooks/useCommentBank';
import { getUserId } from '../../../userLocalDetails';
import { useAvailableYears, useSubjectsForYearsFromClasses, useUserStateSubjects, useTaskTypes, useResponseTypes } from '../../../hooks/useSubjects';
import { useProfile } from '../../../components/state/hooks';
import { useUserStates, useMarkingToolStates } from '../../../hooks/useUserStates';
import { isTeacherRole, isSchoolTeacherRole } from '../../../utils/userAccessHelper';

import ImprovedSecondarySideBar from '../../ImprovedSecondarySideBar';
import MenuButton from '../../MenuButton';
import ScrollableArea from '../../Shared/ScrollableArea';
import { isTabletView } from '../../ReactiveRender';
import FeedbackArea from '../FeedbackArea';
import ItemSettingsModal from '../../Shared/ItemSettingsModal';
import SettingsTagsDisplay from '../../Shared/SettingsTagsDisplay';

import editIcon from '../../../static/img/markEdit.svg';
import EditHover from '../../../static/img/EditHover.svg';

const CommentBankFeedbackAreas = () => {
  const { commentBankId } = useParams();
  const history = useHistory();
  const [isShowMenu, setShowMenu] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const tabletView = isTabletView();
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedTaskTypes, setSelectedTaskTypes] = useState([]);
  const [selectedResponseTypes, setSelectedResponseTypes] = useState([]);

  const { data: profile } = useProfile();
  const { states: userStates } = useUserStates();
  const state = userStates[0] || null;
  const {
    commentBank,
    isLoading: isLoadingCommentBank,
    updateSmartAnnotationHandler,
    deleteAnnotationHandler,
    createSmartAnnotationHandler,
    updateCommentBank,
    reorderSmartCommentsHandler
  } = useCommentBank(commentBankId);

  const resolvedOwnerId = commentBank?.ownerId != null && typeof commentBank.ownerId === 'object'
    ? (commentBank.ownerId?.value ?? commentBank.ownerId?.id ?? '')
    : (commentBank?.ownerId ?? '');
  const canEdit = (resolvedOwnerId && resolvedOwnerId === getUserId()) ||
    isTeacherRole(profile) ||
    isSchoolTeacherRole(profile);

  useEffect(() => {
    if (commentBank) {
      const meta = commentBank.metadata || {};
      setSelectedYears(meta.years?.length ? meta.years : (commentBank.years || []));
      setSelectedSubjects(meta.subjects?.length ? meta.subjects : (commentBank.subjects || []));
      setSelectedTaskTypes(meta.taskTypes?.length ? meta.taskTypes : (commentBank.taskTypes || (commentBank.taskType ? [commentBank.taskType] : [])));
      setSelectedResponseTypes(meta.responseTypes?.length ? meta.responseTypes : (commentBank.responseTypes || (commentBank.responseType ? [commentBank.responseType] : [])));
    }
  }, [commentBank]);

  // Individual-teacher state scope (empty for school/stateless users → no-op).
  // This detail editor feeds ItemSettingsModal's subject picker, so it is scoped
  // like the create pages; the existing-selection reinsert below preserves an
  // edited bank's out-of-state subject.
  const scopeStates = useMarkingToolStates();
  const { years: availableYears } = useAvailableYears();
  const { subjects: subjectsForSelectedYears } = useSubjectsForYearsFromClasses(selectedYears, scopeStates);
  const { subjects: allClassSubjects } = useUserStateSubjects(scopeStates);
  const subjectsSource = selectedYears?.length
    ? (subjectsForSelectedYears?.length ? subjectsForSelectedYears : allClassSubjects)
    : allClassSubjects;
  const { taskTypes: filteredTaskTypes } = useTaskTypes(
    selectedSubjects,
    state,
    selectedYears?.length ? selectedYears : undefined
  );
  const { responseTypes: filteredResponseTypes } = useResponseTypes(
    selectedSubjects,
    selectedTaskTypes,
    state,
    selectedYears?.length ? selectedYears : undefined
  );

  const getSubjectCode = (s) => {
    if (typeof s === 'object') return s.code || s.subjectCode || s.value || s;
    return s;
  };

  const getSubjectTitle = (s) => {
    if (typeof s === 'object') return s.title || s.subjectName || s.name || s.label || '';
    return '';
  };

  // Build a code→title map from all sources so we never lose labels
  const subjectTitleMap = {};
  (subjectsSource || []).forEach(s => {
    const code = s.code || s.subjectCode;
    const title = s.title || s.subjectName || s.name;
    if (code && title) subjectTitleMap[code] = title;
  });
  (selectedSubjects || []).forEach(s => {
    const code = getSubjectCode(s);
    const title = getSubjectTitle(s);
    if (code && title && !subjectTitleMap[code]) subjectTitleMap[code] = title;
  });

  const availableSubjects = (() => {
    const fromSource = (subjectsSource || []).map(s => ({
      value: s.code || s.subjectCode,
      label: s.title || s.subjectName || s.name || s.code || s.subjectCode
    }));
    // Ensure selected subjects are always in the options so chips display correctly
    const existingValues = new Set(fromSource.map(s => s.value));
    const selectedCodes = (selectedSubjects || []).map(getSubjectCode).filter(Boolean);
    selectedCodes.forEach(code => {
      if (!existingValues.has(code)) {
        fromSource.push({ value: code, label: subjectTitleMap[code] || code });
      }
    });
    return fromSource;
  })();

  // No subjects = no task types; no subjects or no task types = no response types
  const taskTypes = selectedSubjects.length
    ? (filteredTaskTypes || []).map(t => ({ ...t, label: t.label ?? t.title }))
    : [];
  const responseTypes = (selectedSubjects.length && selectedTaskTypes.length)
    ? (filteredResponseTypes || [])
    : [];

  // Auto-remove selected task types that are no longer valid
  useEffect(() => {
    if (!selectedTaskTypes.length) return;
    if (!taskTypes.length) { setSelectedTaskTypes([]); return; }
    const validValues = new Set(taskTypes.map(t => t.value ?? t));
    const filtered = selectedTaskTypes.filter(t => validValues.has(t));
    if (filtered.length !== selectedTaskTypes.length) setSelectedTaskTypes(filtered);
  }, [taskTypes.length, selectedSubjects]);

  // Auto-remove selected response types that are no longer valid
  useEffect(() => {
    if (!selectedResponseTypes.length) return;
    if (!responseTypes.length) { setSelectedResponseTypes([]); return; }
    const validValues = new Set(responseTypes.map(r => r.value ?? r));
    const filtered = selectedResponseTypes.filter(r => validValues.has(r));
    if (filtered.length !== selectedResponseTypes.length) setSelectedResponseTypes(filtered);
  }, [responseTypes.length, selectedTaskTypes]);
  const subjects = subjectsSource || [];

  const handleTitleClick = () => {
    if (!canEdit) return;
    // Don't reset the draft title (and the caret) when clicks inside the
    // open input bubble back up to the container — only enter edit mode
    // from the non-editing state (JED-1049).
    if (isEditingTitle) return;
    setIsEditingTitle(true);
    setEditTitle(commentBank.title);
  };

  const handleTitleChange = (event) => {
    setEditTitle(event.target.value);
  };

  const handleTitleKeyPress = (event) => {
    if (event.key === 'Enter') {
      saveTitle();
    }
  };

  const handleTitleBlur = () => {
    saveTitle();
  };

  const saveTitle = async () => {
    if (isEditingTitle) {
      if (!editTitle.trim() || editTitle === commentBank.title) {
        setIsEditingTitle(false);
        return;
      }

      try {
        await updateCommentBank({ title: editTitle.trim() });
      } catch (error) {
        console.error('Error updating comment bank title:', error);
      }

      setIsEditingTitle(false);
    }
  };

  const handleSaveSettings = async (settings) => {
    try {
      // Convert subjects to API format: { subjectCode, subjectName }
      const subjectObjects = (settings.subjects || []).map(s => {
        if (typeof s === 'object' && s !== null) {
          // If already has subjectCode/subjectName, return as-is
          if (s.subjectCode && s.subjectName) {
            return s;
          }
          // Convert {code, title} or {value, label} to {subjectCode, subjectName}
          return {
            subjectCode: s.subjectCode || s.code || s.value,
            subjectName: s.subjectName || s.name || s.title || s.label
          };
        }
        // If it's a string/code, look up the full subject object
        const found = subjects.find(subj => getSubjectCode(subj) === s);
        if (found) {
          return {
            subjectCode: found.code || found.subjectCode || found.value,
            subjectName: found.title || found.subjectName || found.name || found.label
          };
        }
        return null;
      }).filter(Boolean);

      await updateCommentBank({
        years: settings.years,
        subjects: subjectObjects,
        taskTypes: settings.taskTypes,
        responseTypes: settings.responseTypes
      });
      setSelectedYears(settings.years || []);
      setSelectedSubjects(subjectObjects);
      setSelectedTaskTypes(settings.taskTypes || []);
      setSelectedResponseTypes(settings.responseTypes || []);
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  
  if (isLoadingCommentBank) {
    return <Loader />;
  }

  if (!commentBank) {
    return (
      <div>
        Comment bank not found
      </div>
    );
  }

  return (
    <MainContainer>
      <ImprovedSecondarySideBar
        isShowMenu={isShowMenu}
        setShowMenu={setShowMenu}
        id={commentBankId}
      />
      <InnerContainer>
        {tabletView && (
          <HeadingAndFilterContainer>
            <MenuButton setShowMenu={setShowMenu} />
          </HeadingAndFilterContainer>
        )}

        <RightContainer>
          <ContentContainer>
            <TitleSection>
            <TitleRow>
              <BackArrow onClick={() => history.push('/tools/commentbanks')}>&lt;</BackArrow>
              <TitleContainer onClick={handleTitleClick}>
                {isEditingTitle ? (
                  <TitleInput
                    value={editTitle}
                    onChange={handleTitleChange}
                    onBlur={handleTitleBlur}
                    onKeyPress={handleTitleKeyPress}
                    autoFocus
                  />
                ) : (
                  <>
                    <Title>{commentBank.title}</Title>
                    {canEdit && (
                      <EditIconContainer>
                        <EditIconStyled src={editIcon} alt="Edit title" />
                        <EditIconHoverStyled src={EditHover} alt="Edit title" />
                      </EditIconContainer>
                    )}
                  </>
                )}
              </TitleContainer>
            </TitleRow>
            </TitleSection>
            <DetailsStrip>
              <SettingsTagsDisplay
                years={selectedYears}
                subjects={selectedSubjects}
                taskTypes={selectedTaskTypes}
                responseTypes={selectedResponseTypes}
                taskTypeLabels={Object.fromEntries(taskTypes.map(t => [t.value, t.label]))}
                responseTypeLabels={Object.fromEntries(responseTypes.map(r => [r.value, r.label]))}
                onSettingsClick={canEdit ? () => setIsSettingsOpen(!isSettingsOpen) : undefined}
                showSettingsButton={canEdit}
              />
            </DetailsStrip>

            <MainContentSection>
            <FeedbackArea
              smartAnnotation={commentBank}
              UpdateSmartAnotationHandler={updateSmartAnnotationHandler}
              deleteAnnotationHandler={deleteAnnotationHandler}
              createSmartAnnotationHandler={createSmartAnnotationHandler}
              reorderSmartCommentsHandler={reorderSmartCommentsHandler}
              smartAnnotationeditIndex={0}
            />
            </MainContentSection>

            <ItemSettingsModal
              isOpen={isSettingsOpen}
              onClose={() => setIsSettingsOpen(false)}
              onSave={handleSaveSettings}
              availableYears={availableYears}
              availableSubjects={availableSubjects}
              taskTypes={taskTypes}
              responseTypes={responseTypes}
              selectedYears={selectedYears}
              setSelectedYears={setSelectedYears}
              selectedSubjects={selectedSubjects.map(getSubjectCode)}
              setSelectedSubjects={(values) => setSelectedSubjects(values.map(v => {
                const found = subjects.find(s => (s.code === v || s.subjectCode === v || s.value === v || getSubjectCode(s) === v));
                if (found) return found;
                const title = subjectTitleMap[v] || v;
                return { code: v, title };
              }))}
              selectedTaskTypes={selectedTaskTypes}
              setSelectedTaskTypes={setSelectedTaskTypes}
              selectedResponseTypes={selectedResponseTypes}
              setSelectedResponseTypes={setSelectedResponseTypes}
            />
          </ContentContainer>
        </RightContainer>
      </InnerContainer>
    </MainContainer>
  );
};

// Styled Components
const MainContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const RightContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: #f8f9fa;
`;

const ContentContainer = styled(ScrollableArea).attrs({ autoHide: 'leave' })`
  flex: 1;
  padding: 24px 0;
`;

const TitleSection = styled.div`
  padding: 0 24px;
`;

const MainContentSection = styled.div`
  padding: 0 24px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  margin-right: 8px;
  cursor: pointer;
  flex: 1;
  min-width: 0;

  &:hover {
    opacity: 0.8;
  }
`;

const Title = styled.h1`
  font-family: IBM Plex Sans;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  color: #1e252a;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TitleInput = styled.input`
  font-family: IBM Plex Sans;
  font-size: 24px;
  font-weight: 600;
  border: none;
  outline: none;
  background: transparent;
  margin: 0;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  color: #1e252a;
`;

const EditIconStyled = styled.img`
  width: 20px;
  height: 20px;
  opacity: 0.85;
  flex-shrink: 0;
  display: block;

  ${TitleContainer}:hover & {
    display: none;
  }
`;

const EditIconHoverStyled = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: none;

  ${TitleContainer}:hover & {
    display: block;
  }
`;

const EditIconContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const HeadingAndFilterContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #C9C6CC80;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  min-width: 0;
  width: 100%;
`;

const BackArrow = styled.span`
  font-family: IBM Plex Sans;
  font-size: 20px;
  font-weight: 400;
  color: #7200E0;
  cursor: pointer;
  padding: 4px 8px;
  flex-shrink: 0;
  &:hover {
    opacity: 0.8;
  }
`;

const DetailsStrip = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 24px;
  margin: 4px 0 24px 0;
  background: #F5F0FF;
  border-radius: 0;
  border: 1px solid #E8E0F5;
  box-sizing: border-box;
  overflow: hidden;
  isolation: isolate;
`;


export default CommentBankFeedbackAreas;
